import { theme } from '@features/executive-report/domain/theme';
import {
  TextProps,
  TextPropsTextConfig,
  parseContent,
} from '@features/powerpoint';

const SUBTEXT_FONT_SIZE = 10;
const BOX_TEXT_FONT_SIZE = 9;

const taglineHeader = (
  text: string,
  options: TextProps['options'] = {}
): TextProps => ({
  text,
  options: {
    color: theme.colors.inmarketGreen,
    fontSize: 18,
    bold: true,
    h: 0.38,
    w: 5.03,
    x: 1.64,
    y: 2.76,
    ...options,
  },
});

const taglineSubtext = (
  text: string,
  options: TextProps['options'] = {}
): TextProps => ({
  text,
  options: {
    color: theme.colors.white,
    fontSize: SUBTEXT_FONT_SIZE,
    h: 0.44,
    w: 4.17,
    x: 1.64,
    y: 3.13,
    ...options,
  },
});

const lciBoxHeader = (text: string): TextPropsTextConfig => ({
  text: text.toUpperCase(),
  options: {
    fontSize: 12,
    color: theme.colors.inmarketGreen,
    bold: true,
    breakLine: true,
  },
});

const lciBoxText = (
  text: [string, string],
  options: TextProps['options'] = {}
): TextProps => ({
  text: [
    lciBoxHeader(text[0]),
    {
      text: text[1],
      options: {
        fontSize: BOX_TEXT_FONT_SIZE,
        color: theme.colors.white,
      },
    },
  ],
  options: {
    h: 0.45,
    w: 3.27,
    x: 8.32,
    y: 2.88,
    ...options,
  },
});

const titleText: TextProps = {
  text: parseContent('Incrementality <lci>Matters</lci>', 0, {
    fontSize: 44,
    color: theme.colors.white,
  }),
  options: {
    h: 1.58,
    w: 4.46,
    x: 0.76,
    y: 0.93,
    charSpacing: -1.5,
  },
};

const poweredByText: TextProps = {
  text: [
    {
      text: 'Powered by'.toUpperCase(),
      options: {
        fontSize: 8,
        color: theme.colors.white,
        breakLine: true,
      },
    },
    {
      text: 'Lift Conversion'.toUpperCase(),
      options: {
        fontSize: 11,
        color: theme.colors.white,
        bold: true,
        breakLine: true,
      },
    },
    {
      text: 'Index'.toUpperCase(),
      options: {
        fontSize: 11,
        color: theme.colors.white,
        bold: true,
      },
    },
    {
      text: '®',
      options: {
        fontSize: 11,
        color: theme.colors.white,
        superscript: true,
      },
    },
  ],
  options: {
    h: 0.62,
    w: 2.27,
    x: 9.94,
    y: 1.77,
  },
};

export const attributionText = (liftText: string): TextProps => ({
  text: [
    lciBoxHeader('Attribution'),
    {
      text: 'LCI measures ',
      options: {
        fontSize: BOX_TEXT_FONT_SIZE,
        color: theme.colors.darkBlue,
      },
    },
    {
      text: 'Incremental ',
      options: {
        fontSize: BOX_TEXT_FONT_SIZE,
        color: theme.colors.darkBlue,
        bold: true,
      },
    },
    {
      text: liftText,
      options: {
        fontSize: BOX_TEXT_FONT_SIZE,
        color: theme.colors.darkBlue,
      },
    },
  ],
  options: {
    h: 0.75,
    w: 3.41,
    x: 8.32,
    y: 4.94,
    valign: 'top',
  },
});

export const texts: TextProps[] = [
  titleText,
  taglineHeader('Measure omnichannel performance'),
  taglineSubtext(
    'Performance across creative, channels, locations and audiences provides program insights'
  ),
  taglineHeader('Optimize in real time', { y: 4.09 }),
  taglineSubtext(
    "Don't wait until the campaign ends to review results, InMarket informs optimizations in flight",
    { y: 4.42 }
  ),
  taglineHeader('Minimize media waste', { y: 5.44 }),
  taglineSubtext(
    'Discover what matters, reach key KPIs, and drive incremental store visits and sales',
    { y: 5.82 }
  ),
  poweredByText,
  lciBoxText(['Exposure', 'Audience is exposed to a campaign']),
  lciBoxText(
    ['Conversion', 'Audience visits a store and/or makes a purchase'],
    {
      y: 4.02,
    }
  ),
];
